import styles from '../marketplace.module.css'

import {Heading, Pagination, Text, Box} from '@primer/react'
import {SearchIcon} from '@primer/octicons-react'
import {Blankslate} from '@primer/react/experimental'
import {useCallback, useState, useMemo} from 'react'
import {useSearchParams} from '@github-ui/use-navigate'
import {SafeHTMLBox, type SafeHTMLString} from '@github-ui/safe-html'
import Filters from './Filters'
import MarketplaceItem from './MarketplaceItem'
import type {IndexPayload, Category} from '../types'

type Props = {
  searchResults: IndexPayload['searchResults']
  categories: {
    apps: Category[]
    actions: Category[]
  }
}

export default function SearchResults({searchResults, categories}: Props) {
  const [currentPage, setCurrentPage] = useState(1)
  const [searchParams, setSearchParams] = useSearchParams()

  useMemo(() => {
    if (searchParams.has('page')) {
      const page = Number(searchParams.get('page'))
      setCurrentPage(page)
    } else {
      setCurrentPage(1)
    }
  }, [searchParams])

  const queryWithoutSort = useMemo(() => {
    const queryString = searchParams.get('query')
    if (!queryString) {
      return ''
    }

    return queryString.replace(/sort:([^ ]*)/, '').trim()
  }, [searchParams])

  const onPageChange = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>, p: number) => {
      e.preventDefault()
      const newParams = new URLSearchParams(searchParams)
      newParams.set('page', p.toString())
      setSearchParams(newParams)
    },
    [searchParams, setSearchParams],
  )

  const categoryNameMapping = useMemo(() => {
    const mapping = new Map<string, Category>()
    for (const category of [...categories.apps, ...categories.actions]) {
      mapping.set(category.slug, category)
    }
    return mapping
  }, [categories.actions, categories.apps])

  const headingText = useMemo(() => {
    if (queryWithoutSort) {
      return `Search results for “${queryWithoutSort}”`
    } else if (searchParams.has('copilot_app')) {
      return 'Copilot Extensions'
    } else if (searchParams.has('category')) {
      const categoryParam = searchParams.get('category')
      const category = categoryParam ? categoryNameMapping.get(categoryParam) : undefined
      return category ? `${category.name} apps` : 'Apps'
    } else if (searchParams.get('type') === 'actions') {
      return 'Actions'
    } else if (searchParams.get('type') === 'apps') {
      return 'Apps'
    }
    return 'Search results'
  }, [categoryNameMapping, queryWithoutSort, searchParams])

  const detailText = useMemo(() => {
    if (queryWithoutSort) {
      return `${searchResults.total} ${searchResults.total === 1 ? 'result' : 'results'}`
    } else if (searchParams.has('copilot_app')) {
      return 'Extend Copilot capabilities using third party tools, services, and data'
    } else if (searchParams.has('category')) {
      const categoryParam = searchParams.get('category')
      const category = categoryParam ? categoryNameMapping.get(categoryParam) : undefined
      return category ? <SafeHTMLBox html={category.description_html as SafeHTMLString} /> : ''
    } else if (searchParams.get('type') === 'actions') {
      return 'Automate your workflow from idea to production'
    } else if (searchParams.get('type') === 'apps') {
      return 'Build on your workflow with apps that integrate with GitHub'
    }
  }, [categoryNameMapping, queryWithoutSort, searchParams, searchResults.total])

  return (
    <>
      <div className="d-flex flex-justify-between flex-items-end flex-wrap gap-3">
        <div>
          <Heading as="h2" sx={{fontSize: searchResults.results.length > 0 ? 24 : 32}}>
            {headingText}
          </Heading>
          {detailText && searchResults.results.length > 0 && <Text className="fgColor-muted">{detailText}</Text>}
        </div>
        {searchResults.results.length > 0 && <Filters />}
      </div>
      {searchResults.results.length > 0 ? (
        <>
          <div className={`mt-4 ${styles['marketplace-list-grid']}`}>
            {searchResults.results.map(listing => (
              <MarketplaceItem key={`${listing.type}-${listing.id}`} listing={listing} isFeatured={false} />
            ))}
          </div>
          {searchResults.totalPages > 1 && (
            <Pagination
              pageCount={searchResults.totalPages}
              currentPage={currentPage}
              onPageChange={onPageChange}
              showPages={{
                narrow: false,
              }}
            />
          )}
        </>
      ) : (
        <Box sx={{mt: 4}}>
          <Blankslate border>
            <Blankslate.Visual>
              <SearchIcon size="medium" />
            </Blankslate.Visual>
            <Blankslate.Heading>No results</Blankslate.Heading>
            <Blankslate.Description>Try searching by different keywords.</Blankslate.Description>
          </Blankslate>
        </Box>
      )}
    </>
  )
}

try{ SearchResults.displayName ||= 'SearchResults' } catch {}